import { useCallback, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { OptionType } from 'src/apps/dashboard/components/navigation-menu/NavigationMenu';

import {
	findBusinessUnitById,
	formatBusinessUnitsForSelect
} from '../helpers/BusinessUnitsHelpers';
import { navigateToBusinessUnit } from '../helpers/NavigationHelpers';
import { extractUnitIdFromPath } from '../helpers/RouteDetectionHelpers';
import { useBusinessUnitsFromConfig } from '../stores/dashboardConfigStore';

interface UseBusinessUnitSelectorReturn {
	unitId: string | null;
	options: OptionType[];
	selectedValue: OptionType;
	handleSelectionChange: (selected: OptionType) => void;
	isReady: boolean;
}

export const useBusinessUnitSelector = (): UseBusinessUnitSelectorReturn => {
	const history = useHistory();
	const { pathname } = useLocation();

	// Extract unitId from the current path since we're outside the route component
	const unitId = extractUnitIdFromPath(pathname);

	const [selectedValue, setSelectedValue] = useState<OptionType>({ value: '', label: '' });

	// Get business units from configuration store
	const { businessUnits } = useBusinessUnitsFromConfig();

	// Update selected value when unitId or businessUnits change
	useEffect(() => {
		if (unitId && businessUnits.length > 0) {
			const currentUnit = findBusinessUnitById(unitId, businessUnits);
			if (currentUnit) {
				setSelectedValue({ value: currentUnit.id, label: currentUnit.name });
			}
		} else if (!unitId) {
			setSelectedValue({ value: '', label: '' });
		}
	}, [unitId, businessUnits]);

	const handleSelectionChange = useCallback(
		(selected: OptionType) => {
			if (selected.value) {
				setSelectedValue(selected);

				// Navigate to the selected business unit
				if (selected.value !== unitId) {
					navigateToBusinessUnit(history, selected.value);
				}
			}
		},
		[history, unitId]
	);

	const options = formatBusinessUnitsForSelect(businessUnits, unitId);
	const isReady = Boolean(unitId);

	return {
		unitId,
		options,
		selectedValue,
		handleSelectionChange,
		isReady
	};
};
